import { useState } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { CustomButton } from "@/components/custom/custom-button";
import { DataTable } from "@/components/custom/data-table";
import { useInterceptionRules, useToggleInterceptionRule, useDeactivateInterceptionRule } from "@/hooks/use-interception-rules";
import { useRuntimeConfig } from "@/hooks/use-runtime-config";
import { getRuleColumns } from "./_components/rule-columns";
import { getConfigColumns } from "./_components/config-columns";
import RuleFormDialog from "./_components/rule-form-dialog";
import ConfigFormDialog from "./_components/config-form-dialog";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const PayoutPage = () => {
  const { data: rulesRes, isLoading: rulesLoading, isError: rulesError } = useInterceptionRules();
  const { data: configRes, isLoading: configLoading, isError: configError } = useRuntimeConfig();
  const toggleMutation = useToggleInterceptionRule();
  const deactivateMutation = useDeactivateInterceptionRule();

  const [ruleDialog, setRuleDialog] = useState({ open: false, rule: null });
  const [configDialog, setConfigDialog] = useState({ open: false, config: null });
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const rules = rulesRes?.data ?? rulesRes ?? [];
  const configs = configRes?.data ?? configRes ?? [];

  return (
    <Stack spacing={3}>
      <Card>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={600}>Interception Rules</Typography>
            <Typography variant="body2" color="text.secondary">
              Thresholds and time windows gating money movement.
            </Typography>
          </Box>
          <CustomButton onClick={() => setRuleDialog({ open: true, rule: null })} sx={{ height: 35 }}>
            New Rule
          </CustomButton>
        </Stack>

        <DataTable
          data={rules}
          columns={getRuleColumns({
            onEdit: (rule) => setRuleDialog({ open: true, rule }),
            onToggle: (rule) => toggleMutation.mutate(rule.id),
            onDeactivate: (rule) => setDeactivateTarget(rule),
          })}
          isLoading={rulesLoading}
          isError={rulesError}
        />
      </Card>

      <Card>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={600}>Runtime Config</Typography>
            <Typography variant="body2" color="text.secondary">
              Security feature toggles and system-wide settings.
            </Typography>
          </Box>
          <CustomButton onClick={() => setConfigDialog({ open: true, config: null })} sx={{ height: 35 }}>
            Add Config
          </CustomButton>
        </Stack>

        <DataTable
          data={configs}
          columns={getConfigColumns({ onEdit: (config) => setConfigDialog({ open: true, config }) })}
          isLoading={configLoading}
          isError={configError}
        />
      </Card>

      <RuleFormDialog
        open={ruleDialog.open}
        rule={ruleDialog.rule}
        onClose={() => setRuleDialog({ open: false, rule: null })}
      />
      <ConfigFormDialog
        open={configDialog.open}
        config={configDialog.config}
        onClose={() => setConfigDialog({ open: false, config: null })}
      />

      <CustomConfirmDialog
        open={Boolean(deactivateTarget)}
        onClose={() => setDeactivateTarget(null)}
        title="Deactivate rule?"
        description={`This soft-deletes "${deactivateTarget?.description ?? deactivateTarget?.ruleType ?? ""}". The record is kept for audit but removed from the active list.`}
        confirmLabel="Deactivate"
        variant="danger"
        mutation={deactivateMutation}
        onConfirm={() => deactivateMutation.mutate(deactivateTarget.id)}
      />
    </Stack>
  );
};

export default PayoutPage;